import React, { createRef } from 'react';
import { connect } from 'react-redux';
import { Box, Paper } from '@mui/material';

import { ComponentDesign, GroupedProductionComponentInstance, Station } from '@paragon/contract-library';
import {
  ComponentDesignSchema,
  ContainerRow,
  DataRow,
  GroupedProductionComponentInstanceSchema,
  ModelChildLoader,
  ProductionComponentInstanceSchema,
  RowGroup,
  StationSchema,
} from '@paragon/layout-models';
import { forceArray } from '@paragon/utilities';

import { RootState } from '../../../../store';
import { getFilteredComponentDesignsForNavigationPanel } from '../../../../store/selectors/componentListFilterSelectors';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import {
  getActiveScheduleLineGroup,
  getGroupedProductionComponentInstancesForActiveScheduleLineGroup,
} from '../../../../store/selectors/scheduleStationSelectors';
import { getCurrentRenderMode } from '../../../../store/slices/camera/selectors';
import { LayoutDebugVisualsTextInput } from '../../../../three/layout/LayoutDebugVisuals';
import { DesignClientGrid, GridMethods } from './DesignClientGrid';
import { PanelControlBar } from './PanelControlBar';
import {
  CustomFeaturePatternTrussDesignSchema,
  CustomProfileTrussDesignSchema,
  getSchemaForComponentDesign,
} from './schemas/CustomTrussDesignSchema';
import { sanitizeForSearch } from './util/sanitizeForSearch';

type StateProps = ReturnType<typeof mapStateToProps>;
type Props = StateProps;

type State = {
  gridHeight: number;

  componentSearchTerm: string;
};

// sort truss envelopes inside component design "container"
const CONTAINER_LIKE_TYPES = ['componentDesign', 'featurePatternTrussDesign', 'profileTrussDesign'];

export const loaders = new ModelChildLoader();

class ExplorerPanel extends React.Component<Props, State> {
  gridRef = createRef<GridMethods>();

  revealTimeout?: ReturnType<typeof setTimeout>;
  lastRevealTarget?: { guid: string; index: number };

  containerRef = createRef<HTMLDivElement>();
  containerObserver!: ResizeObserver;

  constructor(props: Props) {
    super(props);

    loaders.add(StationSchema, ComponentDesignSchema, this.stationComponentDesignsLoader);
    loaders.add(StationSchema, CustomFeaturePatternTrussDesignSchema, this.stationComponentDesignsLoader);
    loaders.add(StationSchema, CustomProfileTrussDesignSchema, this.stationComponentDesignsLoader);

    // We don't want this to load anything right now, but we do want the type known to support refreshing the page
    loaders.add(GroupedProductionComponentInstanceSchema, ProductionComponentInstanceSchema, () => []);

    this.state = { gridHeight: 0, componentSearchTerm: '' };
  }

  componentDidMount(): void {
    this.containerObserver = new ResizeObserver(() => {
      this.setState({ gridHeight: this.containerRef.current!.clientHeight });
    });
    this.containerObserver.observe(this.containerRef.current!);

    this.revealActiveComponentIfNeeded();
  }

  componentDidUpdate(): void {
    if (this.props.renderMode === 'design') {
      this.revealActiveComponentIfNeeded();
    } else {
      this.lastRevealTarget = undefined;
    }
  }

  componentWillUnmount(): void {
    this.containerObserver.disconnect();

    clearTimeout(this.revealTimeout);
    this.lastRevealTarget = undefined;
  }

  revealActiveComponentIfNeeded(): void {
    clearTimeout(this.revealTimeout);

    const activeComponentGuid = this.props.activeComponentDesign?.guid;
    if (this.props.renderMode !== 'design' || activeComponentGuid == null || this.state.gridHeight <= 0) return;

    const rows = forceArray(this.getTopLevelRows());
    const activeComponentIndex = rows.findIndex((row) => row instanceof DataRow && row.id === activeComponentGuid);
    if (activeComponentIndex < 0) return;

    const previous = this.lastRevealTarget;
    if (previous?.guid === activeComponentGuid && previous?.index === activeComponentIndex) return;

    this.revealTimeout = setTimeout(() => {
      this.gridRef.current?.focusRow({ type: 'componentDesign', guid: activeComponentGuid });
      this.lastRevealTarget = { guid: activeComponentGuid, index: activeComponentIndex };
    });
  }

  get hasActiveSearch() {
    return this.state.componentSearchTerm.trim().length !== 0;
  }

  getTopLevelRows(): RowGroup | RowGroup[] {
    const { componentDesigns, openContext } = this.props;

    const defaultContainerRowGroup = { containerRow: new ContainerRow(ComponentDesignSchema, 0), dataRows: [] };

    if (openContext?.type === 'schedule-station') {
      if (this.props.activeScheduleLineGroup) {
        const groupModels = (this.props.groupedProductionComponentInstancesForActiveScheduleGroup ?? []).map(
          (item) => ({ type: 'groupedProductionComponentInstance', ...item }),
        );

        const filteredGroups = this.filterItemsIfConfigured(
          groupModels,
          (groupedProductionComponentInstance) => groupedProductionComponentInstance.trussName,
        );

        return DataRow.fromArray(filteredGroups, GroupedProductionComponentInstanceSchema) as DataRow<unknown>[];
      }

      return defaultContainerRowGroup;
    }

    return this.filterItemsIfConfigured(componentDesigns, (design) => design.component.name).map(
      (componentDesign) => new DataRow(componentDesign, getSchemaForComponentDesign(componentDesign)),
    ) as DataRow<unknown>[];
  }

  setSearchTerm = (componentSearchTerm: string) => {
    this.setState({ componentSearchTerm });
  };

  stationComponentDesignsLoader = (station: Station) =>
    this.props.componentDesigns.filter((design) => design.guid in station.componentDesigns);

  filterItemsIfConfigured<T extends ComponentDesign | GroupedProductionComponentInstance>(
    items: T[],
    nameGetter: (t: T) => string,
  ): T[] {
    if (this.hasActiveSearch) {
      const sanitizedTerm = sanitizeForSearch(this.state.componentSearchTerm);
      items = items.filter((design) => sanitizeForSearch(nameGetter(design)).includes(sanitizedTerm));
    }

    return items;
  }

  render() {
    const topLevelRows = this.getTopLevelRows();

    // Note that both minHeight values are required to allow the grid to shrink when the window is resized. `min-width`
    //   for flex items is `auto` by default, meaning that it cannot be smaller than the size of its content.

    return (
      <Box sx={{ marginTop: 1, marginX: 1, display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 350 }}>
        <LayoutDebugVisualsTextInput />

        <PanelControlBar
          searchMessage="Search components"
          searchValue={this.state.componentSearchTerm}
          onSearchChange={this.setSearchTerm}
        />

        <Paper
          variant="outlined"
          elevation={0}
          ref={this.containerRef}
          sx={{ marginX: 1, marginTop: 1, marginBottom: 2, flexGrow: 1, minHeight: 0 }}
        >
          <DesignClientGrid
            ref={this.gridRef}
            height={this.state.gridHeight}
            topLevelRows={topLevelRows}
            loaders={loaders}
            containerLikeTypes={CONTAINER_LIKE_TYPES}
          />
        </Paper>
      </Box>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  return {
    renderMode: getCurrentRenderMode(state),
    activeComponentDesign: getActiveComponentDesign(state),
    componentDesigns: getFilteredComponentDesignsForNavigationPanel(state),
    openContext: state.application.openContext,
    activeScheduleLineGroup: getActiveScheduleLineGroup(state),
    groupedProductionComponentInstancesForActiveScheduleGroup:
      getGroupedProductionComponentInstancesForActiveScheduleLineGroup(state),
  };
};

export default connect(mapStateToProps)(ExplorerPanel);
